import React, { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { RoutineType, RoutineTypeDB } from '../../../../domain/db/RoutineTypeDB'
import { RoutineLevelDB } from '../../../../domain/db/RoutineLevelDB'
import { Button } from '../../../../components/Button'
import { yupResolver } from '@hookform/resolvers/yup/dist/yup'
import { athleteRoutineSchema } from '../validators'
import { useTranslation } from 'react-i18next'
import { LabelProps } from '../../../../components/Label'
import { Container, Stack, Dialog, DialogActions, DialogContent, DialogTitle, Checkbox, FormControlLabel } from '@mui/material'
import api from '../../../../services/api'
import { InputTags } from '../../../../components/InputTags'
import { TextArea } from '../../../../components/TextArea'
import { FormInputText } from 'components/FormInputText'
import { FormInputSelect } from 'components/FormInputSelect'
import { LoadingButton } from '@mui/lab'
import { useAppDispatch } from 'redux/hooks'
import { showMessage } from 'redux/reducers/snackbar/snackbar.actions'
import { FormInputDatePicker } from 'components/FormInputDatePicker'
import moment from 'moment'
import dayjs from 'dayjs'

type RoutineFormProps = {
  id: string
  name: string
  type: string
  level: string
  startedAt: string
  dueDateAt?: string | null
  visibleAfterDueDate?: boolean
  note?: string
  labels?: LabelProps[]
  professionalAthleteId: String
}

export const AthleteRoutineForm = ({ isVisible, athleteRoutine, onClose, professionalAthleteId, onSubmitted, loading }: any) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const { control, handleSubmit, reset, setValue, formState: { errors } }: any = useForm<RoutineFormProps>({
    resolver: yupResolver(athleteRoutineSchema), defaultValues: { professionalAthleteId }
  })
  const [started, setStarted] = useState(dayjs())
  const [dueDate, setDueDate] = useState<any | undefined>(undefined)
  const [visibleAfterDueDate, setVisibleAfterDueDate] = useState(false)
  const [isCheckDate, setIsCheckDate] = useState(false)
  const [dueDateAtMessageError, setDueDateAtMessageError] = useState('')
  const [visibleAfterDueDateMessageError, setVisibleAfterDueDateMessageError] = useState('')

  useEffect(() => {
    if (isVisible) {
      reset()
      setStarted(dayjs())
      setDueDateAtMessageError('')
    }
    dataFetch().catch(console.error)
  }, [isVisible, athleteRoutine])

  useEffect(() => {
    if (isCheckDate) {
      setVisibleAfterDueDateMessageError('')
      setDueDateAtMessageError('')
      if (visibleAfterDueDate && !dueDate) {
        setVisibleAfterDueDateMessageError(`${t('routine.checkVisibleAfterDueDateAndDueDateEmpty')}`)
      }
      if (dueDate !== undefined && dayjs(dueDate) && dayjs(dueDate) <= dayjs(started)) {
        setDueDateAtMessageError(`${t('routine.invalidPeriod')}`)
      }
    }
    setIsCheckDate(false)
  }, [isCheckDate])

  const dataFetch = async () => {
    if (athleteRoutine == null) return
    const fields = ['id', 'name', 'type', 'level', 'startedAt', 'dueDateAt', 'visibleAfterDueDate', 'note', 'labels', 'professionalAthleteId']
    fields.forEach(
      (field: any) => {
        if (athleteRoutine[field] !== '' && athleteRoutine[field] !== undefined &&
          (field === 'startedAt' || field === 'dueDateAt')
        ) {
          setValue(field, dayjs(athleteRoutine[field]))
          field === 'startedAt' ? setStarted(dayjs(athleteRoutine.startedAt)) : setDueDate(dayjs(athleteRoutine.dueDateAt))
          return
        }
        setValue(field, athleteRoutine[field])
      }
    )
    setIsCheckDate(true)
  }

  const handleUpsertRoutine = async (athleteRoutine: RoutineFormProps): Promise<void> => {
    if (dueDateAtMessageError || visibleAfterDueDateMessageError) {
      return
    }
    athleteRoutine.startedAt = moment(athleteRoutine.startedAt).format('YYYY-MM-DD')
    if (athleteRoutine.dueDateAt === 'Invalid Date' || athleteRoutine.dueDateAt === '') {
      athleteRoutine.dueDateAt = null
    } else if (athleteRoutine.dueDateAt !== null) {
      athleteRoutine.dueDateAt = moment(athleteRoutine.dueDateAt).format('YYYY-MM-DD')
    }
    try {
      let saveMessage = t('routine.create_success')
      if (athleteRoutine.id !== undefined) {
        saveMessage = t('routine.update_success')
        await api.put(`/professional-athlete-routines/${athleteRoutine.id}`, athleteRoutine)
      } else {
        await api.post('/professional-athlete-routines', athleteRoutine)
      }
      onSubmitted()
      dispatch(showMessage({ message: saveMessage }))
    } catch (e: any) {
      dispatch(showMessage({ message: `${t('general.error')}`, type: 'error' }))
    }
  }

  return (
    <Dialog
      fullWidth={true}
      maxWidth="sm"
      open={isVisible}
      onClose={onClose}
      aria-labelledby="customized-dialog-title"
    >
      <DialogTitle>{(athleteRoutine ? t('routine.editRoutine') : t('routine.newRoutine'))}</DialogTitle>
      <DialogContent>
        <Container component="main">
          <Stack sx={{ mt: 3 }}>
            <Controller
              name="name"
              control={control}
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <FormInputText
                  name="name"
                  value={value}
                  control={control}
                  placeholder={`${t('routine.field.name.placeholder')}`}
                  label={`${t('routine.field.name.label')}`}
                  onChange={onChange}
                  errorMessage={errors.name?.message}
                />
              )} />
          </Stack>
          <Stack sx={{ mt: 3 }}>
            <Controller
              control={control}
              name="type"
              defaultValue={RoutineType.EMPTY}
              render={({ field: { onChange, value } }) => (
                <FormInputSelect
                  name="type"
                  value={value}
                  control={control}
                  onChange={onChange}
                  placeholder={`${t('routine.field.type.placeholder')}`}
                  label={`${t('routine.field.type.label')}`}
                  items={RoutineTypeDB}
                  errorMessage={errors.type?.message}
                />
              )} />
          </Stack>
          <Stack sx={{ mt: 3 }}>
            <Controller
              control={control}
              name="level"
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <FormInputSelect
                  name="level"
                  value={value}
                  control={control}
                  onChange={onChange}
                  placeholder={`${t('routine.field.level.placeholder')}`}
                  label={`${t('routine.field.level.label')}`}
                  items={RoutineLevelDB}
                  errorMessage={errors.level?.message}
                />
              )} />
          </Stack>
          <Stack sx={{ mt: 3 }}>
            <FormInputDatePicker
              name="startedAt"
              required={true}
              control={control}
              label={`${t('routine.field.startedAt.label')}`}
              onAction={(value) => {
                setStarted(value)
                setIsCheckDate(true)
              }}
              onClear={true}
              errorMessage={errors.startedAt?.message}
            />
          </Stack>
          <Stack sx={{ mt: 3 }}>
            <FormInputDatePicker
              name="dueDateAt"
              control={control}
              onClear={true}
              onAction={(value) => {
                console.log(`${value} -  new`)
                setDueDate(value)
                setIsCheckDate(true)
              }}
              label={`${t('routine.field.dueDateAt.label')}`}
              errorMessage={dueDateAtMessageError || visibleAfterDueDateMessageError}
            />
          </Stack>
          <Stack sx={{ mt: 3 }}>
            <FormControlLabel
              control={
                <Controller
                  name="visibleAfterDueDate"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Checkbox
                      checked={value ?? false}
                      name="visibleAfterDueDate"
                      onChange={(e) => {
                        onChange(e.target.checked)
                        setVisibleAfterDueDate(e.target.checked)
                        setIsCheckDate(true)
                      }}
                    />
                  )}
                />
              }
              label={`${t('routine.field.visibleAfterDueDate.label')}`}
            />
          </Stack>
          <Stack sx={{ mt: 3 }}>
            <Controller
              control={control}
              name="note"
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <TextArea
                  value={value}
                  label={`${t('routine.field.note.label')}`}
                  placeholder={`${t('routine.field.note.placeholder')}`}
                  autoCapitalize="none"
                  onChange={onChange}
                />
              )} />
          </Stack>
          <Stack sx={{ mt: 3 }}>
            <Controller
              control={control}
              defaultValue={[]}
              name="labels"
              render={({ field: { onChange, value } }) => {
                return (
                  <InputTags
                    label={`${t('routine.field.tags.label')}`}
                    value={value}
                    onChange={onChange}
                  />
                )
              }} />
          </Stack>
        </Container>
      </DialogContent>
      <DialogActions>
        <LoadingButton loading={loading} onClick={handleSubmit(handleUpsertRoutine)} variant="contained">
          {t('general.btn.save')}
        </LoadingButton>
        <Button title={t('general.btn.dismiss')} onClick={onClose} variant="contained" color="secondary" />
      </DialogActions>
    </Dialog>
  )
}
